import React from 'react'
import { BadgeCard, type BadgeCardProps } from '@pandiafi/components'
import { Tooltip } from '@pandiafi/design-system'

import type { CoreParkingSpot } from '../../../common/validations/contractParkingSpotsSchema'
import { spotIcons, spotTitle, isDisabledSpot } from './uiSelectors'
import { ParkingSpotTooltip } from './ParkingSpotTooltip'
import { hasOwnReservation, mapStatus } from './parkingSpots.helpers'
import { QueueEntry } from '../../api/apiHooks/useAPIGetParkingQueue'

import { useIsPhone } from 'hooks/useBreakpoint'

type Props = {
  spot: CoreParkingSpot
  addressLabel?: string
  onOpen?: (spot: CoreParkingSpot) => void
  canReserveDisabledSpots?: boolean
  reservationsEnabled?: boolean
  queueItems?: QueueEntry[]
}

export const StaticBadgeCard: React.FC<Props> = ({
  spot,
  addressLabel,
  onOpen,
  canReserveDisabledSpots = true,
  reservationsEnabled = true,
  queueItems,
}) => {
  const { primary, secondary } = spotIcons(spot)
  const isPhone = useIsPhone()

  const title = spotTitle(spot)

  const computedStatus = mapStatus(spot, queueItems)
  const isBlockedDisabledSpot =
    !canReserveDisabledSpots && isDisabledSpot(spot) && !hasOwnReservation(spot)
  const status = isBlockedDisabledSpot ? 'disabled' : computedStatus
  const canOpen =
    !isBlockedDisabledSpot &&
    computedStatus !== 'disabled' &&
    computedStatus !== 'not-in-use' &&
    (reservationsEnabled || hasOwnReservation(spot))
  const open = () => {
    if (!canOpen) return
    onOpen?.(spot)
  }

  return (
    <Tooltip
      tooltipClassName="mx-0 w-56 text-left"
      showHandler={isPhone && !canOpen ? 'click' : 'hover'}
      enforceVisibility={isPhone && canOpen ? false : undefined}
      triggerArea={
        <BadgeCard
          variant="parking-spot"
          icon={primary}
          secondaryIcon={secondary}
          parkingSpot={
            spot.locationDetails ?? String(spot.rentableParkingSpotId ?? '')
          }
          status={status as BadgeCardProps['status']}
          onClick={canOpen ? open : undefined}
          HTMLProps={{
            'aria-label': title,
            role: canOpen ? 'button' : 'img',
            tabIndex: canOpen ? 0 : -1,
            onKeyDown: (e) =>
              canOpen && (e.key === 'Enter' || e.key === ' ') && open(),
          }}
        />
      }
    >
      <ParkingSpotTooltip
        spot={spot}
        addressLabel={addressLabel}
        status={status as BadgeCardProps['status']}
        canReserveDisabledSpots={canReserveDisabledSpots}
      />
    </Tooltip>
  )
}
